import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { GameSelector } from '../GameSelector';
import { getGameSlug } from '../../constants/games';

export type GameKey =
  | 'magic'
  | 'pokemon'
  | 'yugioh'
  | 'one-piece'
  | 'lorcana'
  | 'digimon'
  | 'dragon-ball'
  | 'star-wars'
  | 'fab'
  | 'godzilla'
  | 'grand-archive'
  | 'general';

interface GameConfig {
  id: GameKey;
  name: string;
  shortName: string;
  icon: string;
  defaultLife: number;
  minLife?: number;
  maxLife?: number;
  primaryLabel: string;
  presets?: number[];
  color: {
    badge: string;
    border: string;
    accent: string;
    gradient: string;
  };
}

export const GAME_CONFIGS: Record<GameKey, GameConfig> = {
  magic: {
    id: 'magic',
    name: 'Magic: The Gathering',
    shortName: 'Magic',
    icon: 'fa-fire',
    defaultLife: 20,
    primaryLabel: 'Pontos de Vida',
    presets: [20, 40, 30],
    color: {
      badge: 'bg-orange-500/10 text-orange-400 border-orange-500/30',
      border: 'border-orange-500/30',
      accent: 'text-orange-400',
      gradient: 'from-orange-600/20 to-amber-600/10'
    }
  },
  pokemon: {
    id: 'pokemon',
    name: 'Pokémon TCG',
    shortName: 'Pokémon',
    icon: 'fa-bolt',
    defaultLife: 6,
    minLife: 0,
    maxLife: 6,
    primaryLabel: 'Prêmios Restantes',
    presets: [6, 4],
    color: {
      badge: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30',
      border: 'border-yellow-500/30',
      accent: 'text-yellow-400',
      gradient: 'from-yellow-600/20 to-amber-600/10'
    }
  },
  yugioh: {
    id: 'yugioh',
    name: 'Yu-Gi-Oh!',
    shortName: 'Yu-Gi-Oh!',
    icon: 'fa-eye',
    defaultLife: 8000,
    primaryLabel: 'Life Points (LP)',
    presets: [8000, 4000],
    color: {
      badge: 'bg-amber-500/10 text-amber-400 border-amber-500/30',
      border: 'border-amber-500/30',
      accent: 'text-amber-400',
      gradient: 'from-amber-600/20 to-yellow-600/10'
    }
  },
  'one-piece': {
    id: 'one-piece',
    name: 'One Piece TCG',
    shortName: 'One Piece',
    icon: 'fa-anchor',
    defaultLife: 5,
    minLife: 0,
    maxLife: 6,
    primaryLabel: 'Vidas do Líder',
    presets: [5, 4, 6, 3],
    color: {
      badge: 'bg-red-500/10 text-red-400 border-red-500/30',
      border: 'border-red-500/30',
      accent: 'text-red-400',
      gradient: 'from-red-600/20 to-rose-600/10'
    }
  },
  lorcana: {
    id: 'lorcana',
    name: 'Disney Lorcana',
    shortName: 'Lorcana',
    icon: 'fa-wand-magic-sparkles',
    defaultLife: 0,
    minLife: 0,
    maxLife: 20,
    primaryLabel: 'Lore (História)',
    presets: [0],
    color: {
      badge: 'bg-purple-500/10 text-purple-400 border-purple-500/30',
      border: 'border-purple-500/30',
      accent: 'text-purple-400',
      gradient: 'from-purple-600/20 to-indigo-600/10'
    }
  },
  digimon: {
    id: 'digimon',
    name: 'Digimon Card Game',
    shortName: 'Digimon',
    icon: 'fa-gamepad',
    defaultLife: 5,
    minLife: 0,
    maxLife: 10,
    primaryLabel: 'Cartas de Segurança',
    presets: [5],
    color: {
      badge: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
      border: 'border-blue-500/30',
      accent: 'text-blue-400',
      gradient: 'from-blue-600/20 to-cyan-600/10'
    }
  },
  'dragon-ball': {
    id: 'dragon-ball',
    name: 'Dragon Ball Super / Fusion World',
    shortName: 'Dragon Ball',
    icon: 'fa-dragon',
    defaultLife: 8,
    minLife: 0,
    maxLife: 8,
    primaryLabel: 'Vidas Restantes',
    presets: [8, 7],
    color: {
      badge: 'bg-orange-600/10 text-orange-400 border-orange-600/30',
      border: 'border-orange-600/30',
      accent: 'text-orange-400',
      gradient: 'from-orange-700/20 to-amber-600/10'
    }
  },
  'star-wars': {
    id: 'star-wars',
    name: 'Star Wars: Unlimited',
    shortName: 'Star Wars',
    icon: 'fa-jedi',
    defaultLife: 30,
    minLife: 0,
    maxLife: 35,
    primaryLabel: 'HP da Base',
    presets: [30],
    color: {
      badge: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/30',
      border: 'border-cyan-500/30',
      accent: 'text-cyan-400',
      gradient: 'from-cyan-600/20 to-blue-600/10'
    }
  },
  fab: {
    id: 'fab',
    name: 'Flesh and Blood',
    shortName: 'FaB',
    icon: 'fa-shield-halved',
    defaultLife: 40,
    primaryLabel: 'Vida do Herói',
    presets: [40, 20],
    color: {
      badge: 'bg-rose-500/10 text-rose-400 border-rose-500/30',
      border: 'border-rose-500/30',
      accent: 'text-rose-400',
      gradient: 'from-rose-600/20 to-red-600/10'
    }
  },
  godzilla: {
    id: 'godzilla',
    name: 'Godzilla Card Game',
    shortName: 'Godzilla',
    icon: 'fa-radiation',
    defaultLife: 5,
    minLife: 0,
    maxLife: 10,
    primaryLabel: 'Escudos / Vida',
    presets: [5, 6, 7],
    color: {
      badge: 'bg-emerald-600/10 text-emerald-400 border-emerald-500/30',
      border: 'border-emerald-500/30',
      accent: 'text-emerald-400',
      gradient: 'from-emerald-700/20 to-teal-600/10'
    }
  },
  'grand-archive': {
    id: 'grand-archive',
    name: 'Grand Archive TCG',
    shortName: 'Grand Archive',
    icon: 'fa-shield-heart',
    defaultLife: 20,
    minLife: 0,
    maxLife: 40,
    primaryLabel: 'Vida do Campeão',
    presets: [20, 25, 30],
    color: {
      badge: 'bg-indigo-500/10 text-indigo-400 border-indigo-500/30',
      border: 'border-indigo-500/30',
      accent: 'text-indigo-400',
      gradient: 'from-indigo-600/20 to-purple-600/10'
    }
  },
  general: {
    id: 'general',
    name: 'Padrão / Geral',
    shortName: 'Geral',
    icon: 'fa-dice-d20',
    defaultLife: 20,
    primaryLabel: 'Pontos de Vida',
    presets: [20, 30, 40, 50, 80],
    color: {
      badge: 'bg-slate-500/10 text-slate-300 border-slate-500/30',
      border: 'border-slate-500/30',
      accent: 'text-slate-200',
      gradient: 'from-slate-700/20 to-slate-800/10'
    }
  }
};

// Map activeGame ID from Cardumy to GameKey
export function mapActiveGameToKey(activeGame: number | string | 'All' | null | undefined): GameKey {
  if (!activeGame || activeGame === 'All') return 'general';
  const slug = getGameSlug(activeGame);
  if (slug === 'dragon-ball-fusion') return 'dragon-ball';
  if (slug in GAME_CONFIGS) return slug as GameKey;
  return 'general';
}

interface PlayerExtraState {
  // Magic
  poison: number;
  commanderDamage: number;
  energy: number;
  // Pokemon
  activeDamage: number;
  specialConditions: {
    poison: boolean;
    burn: boolean;
    asleep: boolean;
    confused: boolean;
    paralyzed: boolean;
  };
  vstarUsed: boolean;
  // One Piece
  donActive: number;
  donRested: number;
  powerBoost: number;
  // Star Wars
  initiative: boolean;
  epicActionUsed: boolean;
  // Dragon Ball
  awakened: boolean;
  // Godzilla
  monsterRank: number; // 1 to 4
  godzillaEnergy: number;
  // Grand Archive
  championLevel: number;
  memory: number;
}

const defaultPlayerExtra = (): PlayerExtraState => ({
  poison: 0,
  commanderDamage: 0,
  energy: 0,
  activeDamage: 0,
  specialConditions: {
    poison: false,
    burn: false,
    asleep: false,
    confused: false,
    paralyzed: false
  },
  vstarUsed: false,
  donActive: 0,
  donRested: 0,
  powerBoost: 0,
  initiative: false,
  epicActionUsed: false,
  awakened: false,
  monsterRank: 1,
  godzillaEnergy: 0,
  championLevel: 0,
  memory: 0
});

interface LifeHistoryLog {
  id: string;
  time: string;
  playerName: string;
  prevLife: number;
  newLife: number;
  diff: number;
}

interface GameLifeCounterProps {
  initialGame?: GameKey;
  onExitFullscreen?: () => void;
  onBack?: () => void;
}

export const GameLifeCounter: React.FC<GameLifeCounterProps> = ({
  initialGame = 'magic',
  onBack
}) => {
  const [selectedGame, setSelectedGame] = useState<GameKey>(initialGame);
  const [playerCount, setPlayerCount] = useState<number>(2);
  const [isRotatedP2, setIsRotatedP2] = useState<boolean>(true); // Face to face mode
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [soundEnabled, setSoundEnabled] = useState<boolean>(true);
  const [showGameSelector, setShowGameSelector] = useState<boolean>(false);
  const [showQuickDice, setShowQuickDice] = useState<boolean>(false);
  const [showHistoryModal, setShowHistoryModal] = useState<boolean>(false);
  const [calculatorPlayerIndex, setCalculatorPlayerIndex] = useState<number | null>(null);
  const [calcInput, setCalcInput] = useState<string>('');
  const [diceResult, setDiceResult] = useState<number | null>(null);
  const [coinResult, setCoinResult] = useState<'CARA' | 'COROA' | null>(null);
  const [activeExtraModal, setActiveExtraModal] = useState<number | null>(null);
  const [sharedMemory, setSharedMemory] = useState<number>(0); // For Digimon Memory Gauge (-10 to +10)
  const [recentDeltas, setRecentDeltas] = useState<Record<number, { diff: number; timeoutId: any }>>({});
  const [historyLogs, setHistoryLogs] = useState<LifeHistoryLog[]>([]);

  const config = GAME_CONFIGS[selectedGame] || GAME_CONFIGS.general;

  // Players state
  const [names, setNames] = useState<string[]>(['Jogador 1', 'Jogador 2', 'Jogador 3', 'Jogador 4']);
  const [life, setLife] = useState<number[]>([
    config.defaultLife,
    config.defaultLife,
    config.defaultLife,
    config.defaultLife
  ]);
  const [extra, setExtra] = useState<PlayerExtraState[]>([
    defaultPlayerExtra(),
    defaultPlayerExtra(),
    defaultPlayerExtra(),
    defaultPlayerExtra()
  ]);

  // Audio synthesizer using Web Audio API
  const playSound = useCallback(
    (type: 'up' | 'down' | 'reset' | 'dice' | 'calc') => {
      if (!soundEnabled) return;
      try {
        const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
        if (!AudioCtx) return;
        const ctx = new AudioCtx();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        const now = ctx.currentTime;

        if (type === 'up') {
          osc.frequency.setValueAtTime(420, now);
          osc.frequency.exponentialRampToValueAtTime(740, now + 0.07);
          gain.gain.setValueAtTime(0.08, now);
          gain.gain.exponentialRampToValueAtTime(0.001, now + 0.07);
          osc.start(now);
          osc.stop(now + 0.07);
        } else if (type === 'down') {
          osc.frequency.setValueAtTime(480, now);
          osc.frequency.exponentialRampToValueAtTime(260, now + 0.07);
          gain.gain.setValueAtTime(0.08, now);
          gain.gain.exponentialRampToValueAtTime(0.001, now + 0.07);
          osc.start(now);
          osc.stop(now + 0.07);
        } else if (type === 'reset') {
          osc.frequency.setValueAtTime(320, now);
          osc.frequency.exponentialRampToValueAtTime(580, now + 0.12);
          gain.gain.setValueAtTime(0.1, now);
          gain.gain.exponentialRampToValueAtTime(0.001, now + 0.12);
          osc.start(now);
          osc.stop(now + 0.12);
        } else if (type === 'dice') {
          osc.type = 'triangle';
          osc.frequency.setValueAtTime(520, now);
          gain.gain.setValueAtTime(0.09, now);
          gain.gain.exponentialRampToValueAtTime(0.001, now + 0.05);
          osc.start(now);
          osc.stop(now + 0.05);
        } else if (type === 'calc') {
          osc.frequency.setValueAtTime(600, now);
          gain.gain.setValueAtTime(0.05, now);
          gain.gain.exponentialRampToValueAtTime(0.001, now + 0.03);
          osc.start(now);
          osc.stop(now + 0.03);
        }
      } catch (e) {
        // audio error ignored
      }
    },
    [soundEnabled]
  );

  const triggerHaptic = useCallback(() => {
    if (typeof window !== 'undefined' && 'vibrate' in navigator) {
      try {
        navigator.vibrate(15);
      } catch (e) {}
    }
  }, []);

  // Sync life on game change
  useEffect(() => {
    const cfg = GAME_CONFIGS[selectedGame] || GAME_CONFIGS.general;
    setLife(Array(4).fill(cfg.defaultLife));
    setSharedMemory(0);
    setExtra([defaultPlayerExtra(), defaultPlayerExtra(), defaultPlayerExtra(), defaultPlayerExtra()]);
    setRecentDeltas({});
    setHistoryLogs([]);
  }, [selectedGame]);

  // Keep screen awake during game
  useEffect(() => {
    let wakeLockInstance: any = null;
    if ('wakeLock' in navigator) {
      try {
        (navigator as any).wakeLock.request('screen').then((lock: any) => {
          wakeLockInstance = lock;
        }).catch(() => {});
      } catch (e) {}
    }
    return () => {
      if (wakeLockInstance && wakeLockInstance.release) {
        wakeLockInstance.release().catch(() => {});
      }
    };
  }, []);

  const showDeltaFeedback = useCallback((idx: number, diff: number) => {
    setRecentDeltas(prev => {
      const existing = prev[idx];
      if (existing?.timeoutId) clearTimeout(existing.timeoutId);
      const accumulated = (existing ? existing.diff : 0) + diff;
      const timeoutId = setTimeout(() => {
        setRecentDeltas(curr => {
          const next = { ...curr };
          delete next[idx];
          return next;
        });
      }, 1200);
      return { ...prev, [idx]: { diff: accumulated, timeoutId } };
    });
  }, []);

  const updateLife = useCallback(
    (index: number, diff: number) => {
      triggerHaptic();
      playSound(diff > 0 ? 'up' : 'down');
      showDeltaFeedback(index, diff);

      setLife(prev => {
        const next = [...prev];
        const oldVal = next[index];
        let val = oldVal + diff;
        if (config.minLife !== undefined) val = Math.max(config.minLife, val);
        if (config.maxLife !== undefined) val = Math.min(config.maxLife, val);
        next[index] = val;

        // Log to history
        const now = new Date();
        const timeStr = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`;
        setHistoryLogs(h => [
          {
            id: Math.random().toString(36).substring(2, 9),
            time: timeStr,
            playerName: names[index],
            prevLife: oldVal,
            newLife: val,
            diff: val - oldVal
          },
          ...h.slice(0, 49)
        ]);

        return next;
      });
    },
    [config.minLife, config.maxLife, triggerHaptic, playSound, showDeltaFeedback, names]
  );

  const setExactLife = useCallback(
    (index: number, val: number) => {
      triggerHaptic();
      playSound(val >= life[index] ? 'up' : 'down');

      setLife(prev => {
        const next = [...prev];
        const oldVal = next[index];
        let clamped = val;
        if (config.minLife !== undefined) clamped = Math.max(config.minLife, clamped);
        if (config.maxLife !== undefined) clamped = Math.min(config.maxLife, clamped);
        next[index] = clamped;

        const diff = clamped - oldVal;
        showDeltaFeedback(index, diff);

        const now = new Date();
        const timeStr = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`;
        setHistoryLogs(h => [
          {
            id: Math.random().toString(36).substring(2, 9),
            time: timeStr,
            playerName: names[index],
            prevLife: oldVal,
            newLife: clamped,
            diff
          },
          ...h.slice(0, 49)
        ]);

        return next;
      });
    },
    [config.minLife, config.maxLife, triggerHaptic, playSound, life, showDeltaFeedback, names]
  );

  const resetAll = useCallback(() => {
    triggerHaptic();
    playSound('reset');
    setLife(Array(4).fill(config.defaultLife));
    setSharedMemory(0);
    setExtra([defaultPlayerExtra(), defaultPlayerExtra(), defaultPlayerExtra(), defaultPlayerExtra()]);
    setRecentDeltas({});
  }, [config.defaultLife, triggerHaptic, playSound]);

  const updateExtra = useCallback(
    (playerIndex: number, updater: (prev: PlayerExtraState) => PlayerExtraState) => {
      triggerHaptic();
      playSound('up');
      setExtra(prev => {
        const next = [...prev];
        next[playerIndex] = updater(next[playerIndex]);
        return next;
      });
    },
    [triggerHaptic, playSound]
  );

  // Dice & Coin
  const rollDie = (sides: number) => {
    triggerHaptic();
    playSound('dice');
    const res = Math.floor(Math.random() * sides) + 1;
    setDiceResult(res);
  };

  const flipCoin = () => {
    triggerHaptic();
    playSound('dice');
    setCoinResult(Math.random() > 0.5 ? 'CARA' : 'COROA');
  };

  // Calculator logic
  const handleCalcApply = (isSubtract: boolean) => {
    if (calculatorPlayerIndex === null) return;
    const amount = parseInt(calcInput, 10);
    if (!isNaN(amount) && amount > 0) {
      updateLife(calculatorPlayerIndex, isSubtract ? -amount : amount);
    }
    setCalcInput('');
    setCalculatorPlayerIndex(null);
  };

  // Player themes
  const playerTheme = useMemo(
    () => [
      {
        bg: 'bg-emerald-950/40',
        text: 'text-emerald-400',
        border: 'border-emerald-500/25',
        ring: 'ring-emerald-500/30'
      },
      {
        bg: 'bg-blue-950/40',
        text: 'text-blue-400',
        border: 'border-blue-500/25',
        ring: 'ring-blue-500/30'
      },
      {
        bg: 'bg-purple-950/40',
        text: 'text-purple-400',
        border: 'border-purple-500/25',
        ring: 'ring-purple-500/30'
      },
      {
        bg: 'bg-amber-950/40',
        text: 'text-amber-400',
        border: 'border-amber-500/25',
        ring: 'ring-amber-500/30'
      }
    ],
    []
  );

  // Render game-specific badges/counters for player
  const renderGameBadges = (idx: number) => {
    const pExtra = extra[idx];
    const val = life[idx];

    if (selectedGame === 'magic') {
      return (
        <div className="flex items-center gap-1 flex-wrap">
          {pExtra.poison > 0 && (
            <span className="px-1.5 py-0.5 rounded-full text-[9px] font-black uppercase bg-emerald-950 text-emerald-300 border border-emerald-500/30 flex items-center gap-1">
              <i className="fas fa-skull-crossbones text-[8px]"></i> {pExtra.poison}
            </span>
          )}
          {pExtra.commanderDamage > 0 && (
            <span className="px-1.5 py-0.5 rounded-full text-[9px] font-black uppercase bg-red-950 text-red-300 border border-red-500/30 flex items-center gap-1">
              <i className="fas fa-crown text-[8px]"></i> {pExtra.commanderDamage}
            </span>
          )}
          {pExtra.energy > 0 && (
            <span className="px-1.5 py-0.5 rounded-full text-[9px] font-black uppercase bg-cyan-950 text-cyan-300 border border-cyan-500/30 flex items-center gap-1">
              <i className="fas fa-bolt text-[8px]"></i> {pExtra.energy}
            </span>
          )}
        </div>
      );
    }

    if (selectedGame === 'pokemon') {
      return (
        <div className="flex items-center gap-1 flex-wrap">
          {pExtra.activeDamage > 0 && (
            <span className="px-1.5 py-0.5 rounded-full text-[9px] font-black uppercase bg-red-950 text-red-300 border border-red-500/30">
              Dano: {pExtra.activeDamage}
            </span>
          )}
          {pExtra.vstarUsed && (
            <span className="px-1.5 py-0.5 rounded-full text-[9px] font-black uppercase bg-yellow-950 text-yellow-300 border border-yellow-500/30">
              VSTAR
            </span>
          )}
        </div>
      );
    }

    if (selectedGame === 'one-piece') {
      return (
        <div className="flex items-center gap-1 flex-wrap">
          <span className="px-1.5 py-0.5 rounded-full text-[9px] font-black uppercase bg-red-950 text-red-300 border border-red-500/30">
            DON!! {pExtra.donActive}/10
          </span>
          {pExtra.powerBoost > 0 && (
            <span className="px-1.5 py-0.5 rounded-full text-[9px] font-black uppercase bg-amber-950 text-amber-300 border border-amber-500/30">
              +{pExtra.powerBoost}
            </span>
          )}
        </div>
      );
    }

    if (selectedGame === 'godzilla') {
      const roman = pExtra.monsterRank === 1 ? 'I' : pExtra.monsterRank === 2 ? 'II' : pExtra.monsterRank === 3 ? 'III' : 'IV';
      return (
        <div className="flex items-center gap-1 flex-wrap">
          <span className="px-1.5 py-0.5 rounded-full text-[9px] font-black uppercase bg-emerald-950 text-emerald-300 border border-emerald-500/30 flex items-center gap-1">
            <i className="fas fa-radiation text-[8px] text-emerald-400"></i> Rank {roman}
          </span>
          {pExtra.godzillaEnergy > 0 && (
            <span className="px-1.5 py-0.5 rounded-full text-[9px] font-black uppercase bg-teal-950 text-teal-300 border border-teal-500/30">
              ⚡ {pExtra.godzillaEnergy}
            </span>
          )}
        </div>
      );
    }

    if (selectedGame === 'dragon-ball') {
      const isAwakened = val <= 4 || pExtra.awakened;
      return (
        <div className="flex items-center gap-1">
          {isAwakened ? (
            <span className="px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider bg-amber-500 text-black animate-pulse">
              ⚡ DESPERTADO
            </span>
          ) : (
            <span className="px-1.5 py-0.5 rounded-full text-[9px] font-bold text-slate-400 bg-slate-900 border border-slate-800">
              Desperta c/ 4
            </span>
          )}
        </div>
      );
    }

    if (selectedGame === 'star-wars') {
      return (
        <div className="flex items-center gap-1">
          {pExtra.initiative && (
            <span className="px-1.5 py-0.5 rounded-full text-[9px] font-black uppercase bg-cyan-950 text-cyan-300 border border-cyan-500/30">
              ✦ Iniciativa
            </span>
          )}
          {pExtra.epicActionUsed && (
            <span className="px-1.5 py-0.5 rounded-full text-[9px] font-bold text-slate-500 bg-slate-900 border border-slate-800">
              Líder Usado
            </span>
          )}
        </div>
      );
    }

    if (selectedGame === 'grand-archive') {
      return (
        <div className="flex items-center gap-1">
          <span className="px-1.5 py-0.5 rounded-full text-[9px] font-black uppercase bg-indigo-950 text-indigo-300 border border-indigo-500/30">
            Nível {pExtra.championLevel}
          </span>
          {pExtra.memory > 0 && (
            <span className="px-1.5 py-0.5 rounded-full text-[9px] font-black uppercase bg-blue-950 text-blue-300 border border-blue-500/30">
              Mem: {pExtra.memory}
            </span>
          )}
        </div>
      );
    }

    if (selectedGame === 'lorcana') {
      return (
        <div className="flex items-center gap-1.5">
          <div className="w-16 bg-slate-900 rounded-full h-1.5 overflow-hidden border border-slate-800">
            <div
              className="bg-purple-500 h-full transition-all duration-300"
              style={{ width: `${Math.min(100, (val / 20) * 100)}%` }}
            />
          </div>
          <span className="text-[9px] font-bold text-purple-400">{val}/20</span>
        </div>
      );
    }

    return null;
  };

  // Render quick action buttons per game
  const renderQuickButtons = (idx: number) => {
    if (selectedGame === 'yugioh') {
      return (
        <div className="flex items-center gap-1 justify-center flex-wrap">
          <button
            onClick={() => updateLife(idx, -1000)}
            className="px-2.5 py-1 bg-slate-900/90 hover:bg-slate-800 border border-slate-800 text-red-400 rounded-lg text-[10px] font-black active:scale-95 transition-all"
          >
            -1k
          </button>
          <button
            onClick={() => updateLife(idx, -500)}
            className="px-2 py-1 bg-slate-900/90 hover:bg-slate-800 border border-slate-800 text-red-400 rounded-lg text-[10px] font-black active:scale-95 transition-all"
          >
            -500
          </button>
          <button
            onClick={() => setExactLife(idx, Math.ceil(life[idx] / 2))}
            className="px-2 py-1 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 text-amber-300 rounded-lg text-[10px] font-black active:scale-95 transition-all"
            title="Metade dos LP (Solemn Judgement)"
          >
            ÷2
          </button>
          <button
            onClick={() => {
              setCalcInput('');
              setCalculatorPlayerIndex(idx);
            }}
            className="px-2.5 py-1 bg-purple-600/20 hover:bg-purple-600/30 border border-purple-500/30 text-purple-300 rounded-lg text-[10px] font-black active:scale-95 transition-all flex items-center gap-1"
            title="Calculadora de LP"
          >
            <i className="fas fa-calculator text-[9px]"></i> Calc
          </button>
          <button
            onClick={() => updateLife(idx, 500)}
            className="px-2 py-1 bg-slate-900/90 hover:bg-slate-800 border border-slate-800 text-emerald-400 rounded-lg text-[10px] font-black active:scale-95 transition-all"
          >
            +500
          </button>
          <button
            onClick={() => updateLife(idx, 1000)}
            className="px-2.5 py-1 bg-slate-900/90 hover:bg-slate-800 border border-slate-800 text-emerald-400 rounded-lg text-[10px] font-black active:scale-95 transition-all"
          >
            +1k
          </button>
        </div>
      );
    }

    if (selectedGame === 'lorcana') {
      return (
        <div className="flex items-center gap-1.5 justify-center">
          <button
            onClick={() => updateLife(idx, -1)}
            className="px-3 py-1 bg-slate-900/90 hover:bg-slate-800 border border-slate-800 text-red-400 rounded-lg text-[11px] font-black active:scale-95 transition-all"
          >
            -1
          </button>
          <button
            onClick={() => updateLife(idx, 1)}
            className="px-3 py-1 bg-purple-600/20 hover:bg-purple-600/30 border border-purple-500/30 text-purple-300 rounded-lg text-[11px] font-black active:scale-95 transition-all"
          >
            +1 Lore
          </button>
          <button
            onClick={() => updateLife(idx, 2)}
            className="px-3 py-1 bg-purple-600/20 hover:bg-purple-600/30 border border-purple-500/30 text-purple-300 rounded-lg text-[11px] font-black active:scale-95 transition-all"
          >
            +2 Quest
          </button>
          <button
            onClick={() => updateLife(idx, 3)}
            className="px-3 py-1 bg-purple-600/20 hover:bg-purple-600/30 border border-purple-500/30 text-purple-300 rounded-lg text-[11px] font-black active:scale-95 transition-all"
          >
            +3
          </button>
        </div>
      );
    }

    if (selectedGame === 'one-piece') {
      return (
        <div className="flex items-center gap-1.5 justify-center">
          <button
            onClick={() => updateLife(idx, -1)}
            className="px-3 py-1 bg-slate-900/90 hover:bg-slate-800 border border-slate-800 text-red-400 rounded-lg text-[11px] font-black active:scale-95 transition-all flex items-center gap-1"
          >
            <i className="fas fa-heart-crack text-[9px]"></i> -1 Vida
          </button>
          <button
            onClick={() =>
              updateExtra(idx, prev => ({ ...prev, donActive: Math.min(10, prev.donActive + 2) }))
            }
            className="px-2.5 py-1 bg-red-950/60 hover:bg-red-900/60 border border-red-500/30 text-red-300 rounded-lg text-[10px] font-black active:scale-95 transition-all"
          >
            +2 DON!!
          </button>
          <button
            onClick={() =>
              updateExtra(idx, prev => ({ ...prev, powerBoost: prev.powerBoost + 1000 }))
            }
            className="px-2.5 py-1 bg-amber-950/60 hover:bg-amber-900/60 border border-amber-500/30 text-amber-300 rounded-lg text-[10px] font-black active:scale-95 transition-all"
          >
            +1k Poder
          </button>
        </div>
      );
    }

    if (selectedGame === 'godzilla') {
      return (
        <div className="flex items-center gap-1.5 justify-center">
          <button
            onClick={() => updateLife(idx, -1)}
            className="px-3 py-1 bg-slate-900/90 hover:bg-slate-800 border border-slate-800 text-red-400 rounded-lg text-[11px] font-black active:scale-95 transition-all flex items-center gap-1"
          >
            <i className="fas fa-shield-halved text-[9px]"></i> -1 Escudo
          </button>
          <button
            onClick={() =>
              updateExtra(idx, p => ({
                ...p,
                monsterRank: p.monsterRank >= 4 ? 1 : p.monsterRank + 1
              }))
            }
            className="px-2.5 py-1 bg-emerald-950/60 hover:bg-emerald-900/60 border border-emerald-500/30 text-emerald-300 rounded-lg text-[10px] font-black active:scale-95 transition-all"
          >
            Evoluir Rank
          </button>
          <button
            onClick={() =>
              updateExtra(idx, p => ({ ...p, godzillaEnergy: p.godzillaEnergy + 1 }))
            }
            className="px-2.5 py-1 bg-teal-950/60 hover:bg-teal-900/60 border border-teal-500/30 text-teal-300 rounded-lg text-[10px] font-black active:scale-95 transition-all"
          >
            +1 Energia
          </button>
        </div>
      );
    }

    if (selectedGame === 'pokemon') {
      return (
        <div className="flex items-center gap-1.5 justify-center">
          <button
            onClick={() => updateLife(idx, -1)}
            className="px-3 py-1 bg-yellow-500/20 hover:bg-yellow-500/30 border border-yellow-500/40 text-yellow-300 rounded-lg text-[11px] font-black active:scale-95 transition-all flex items-center gap-1"
          >
            <i className="fas fa-trophy text-[9px]"></i> Pegar Prêmio (-1)
          </button>
          <button
            onClick={() => updateExtra(idx, p => ({ ...p, activeDamage: p.activeDamage + 10 }))}
            className="px-2.5 py-1 bg-red-950/60 hover:bg-red-900/60 border border-red-500/30 text-red-300 rounded-lg text-[10px] font-black active:scale-95 transition-all"
          >
            +10 Dano
          </button>
          <button
            onClick={() => updateExtra(idx, p => ({ ...p, activeDamage: p.activeDamage + 50 }))}
            className="px-2.5 py-1 bg-red-950/60 hover:bg-red-900/60 border border-red-500/30 text-red-300 rounded-lg text-[10px] font-black active:scale-95 transition-all"
          >
            +50 Dano
          </button>
        </div>
      );
    }

    // Default (Magic, FaB, Star Wars, Grand Archive, General)
    return (
      <div className="flex items-center gap-1.5 justify-center">
        <button
          onClick={() => updateLife(idx, -5)}
          className="px-2.5 py-1 bg-slate-900/90 hover:bg-slate-800 border border-slate-800 text-red-400 rounded-lg text-[10px] font-black active:scale-95 transition-all"
        >
          -5
        </button>
        <button
          onClick={() => updateLife(idx, -1)}
          className="px-3 py-1 bg-slate-900/90 hover:bg-slate-800 border border-slate-800 text-red-400 rounded-lg text-[10px] font-black active:scale-95 transition-all"
        >
          -1
        </button>
        <button
          onClick={() => {
            setCalcInput('');
            setCalculatorPlayerIndex(idx);
          }}
          className="w-7 h-6 bg-slate-900/90 hover:bg-slate-800 border border-slate-800 text-slate-300 rounded-lg text-[10px] font-black flex items-center justify-center active:scale-95 transition-all"
          title="Calculadora / Digitar valor"
        >
          <i className="fas fa-calculator text-[9px]"></i>
        </button>
        <button
          onClick={() => updateLife(idx, 1)}
          className="px-3 py-1 bg-slate-900/90 hover:bg-slate-800 border border-slate-800 text-emerald-400 rounded-lg text-[10px] font-black active:scale-95 transition-all"
        >
          +1
        </button>
        <button
          onClick={() => updateLife(idx, 5)}
          className="px-2.5 py-1 bg-slate-900/90 hover:bg-slate-800 border border-slate-800 text-emerald-400 rounded-lg text-[10px] font-black active:scale-95 transition-all"
        >
          +5
        </button>
      </div>
    );
  };

  // Player Single View / Half-screen box
  const renderPlayerZone = (idx: number, isTopPlayerIn2P: boolean = false) => {
    const val = life[idx];
    const name = names[idx];
    const theme = playerTheme[idx % playerTheme.length];
    const shouldRotate = isTopPlayerIn2P && isRotatedP2;
    const delta = recentDeltas[idx];

    // Responsive tap increment: for Yu-Gi-Oh taps are -100/+100, others -1/+1
    const tapStep = selectedGame === 'yugioh' ? 100 : 1;

    return (
      <div
        key={idx}
        className={`relative flex-1 flex flex-col justify-between p-2.5 sm:p-3.5 rounded-2xl border transition-all duration-300 select-none overflow-hidden ${theme.bg} ${theme.border} ${
          shouldRotate ? 'rotate-180' : ''
        }`}
        style={{ touchAction: 'manipulation' }}
      >
        {/* Top bar inside player zone */}
        <div className="flex items-center justify-between gap-1.5 z-10 shrink-0">
          <div className="flex items-center gap-1.5 max-w-[140px] sm:max-w-[200px]">
            <input
              type="text"
              value={name}
              onChange={e => {
                const next = [...names];
                next[idx] = e.target.value;
                setNames(next);
              }}
              maxLength={15}
              className="bg-transparent text-[11px] sm:text-xs font-black uppercase tracking-wider text-white border-b border-white/10 hover:border-white/30 focus:border-purple-400 outline-none w-full"
            />
          </div>

          <div className="flex items-center gap-1">
            {renderGameBadges(idx)}
            <button
              onClick={() => setActiveExtraModal(idx)}
              className="w-6 h-6 sm:w-7 sm:h-7 rounded-lg bg-slate-900/80 hover:bg-slate-800 border border-white/10 text-slate-300 flex items-center justify-center text-[10px] active:scale-90 transition-all shrink-0"
              title="Mais opções e marcadores"
            >
              <i className="fas fa-ellipsis-v"></i>
            </button>
          </div>
        </div>

        {/* Center: Giant Touchable Life Display */}
        <div className="relative flex-1 flex items-center justify-center my-0.5 group">
          {/* Left tap area (subtraction) */}
          <div
            onClick={() => updateLife(idx, -tapStep)}
            className="absolute left-0 top-0 bottom-0 w-1/2 cursor-pointer flex items-center justify-start pl-3 sm:pl-6 opacity-0 hover:opacity-100 active:opacity-100 transition-opacity z-10"
          >
            <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-full bg-red-500/20 border border-red-500/40 text-red-400 flex items-center justify-center text-base sm:text-lg pointer-events-none">
              <i className="fas fa-minus"></i>
            </div>
          </div>

          {/* Right tap area (addition) */}
          <div
            onClick={() => updateLife(idx, tapStep)}
            className="absolute right-0 top-0 bottom-0 w-1/2 cursor-pointer flex items-center justify-end pr-3 sm:pr-6 opacity-0 hover:opacity-100 active:opacity-100 transition-opacity z-10"
          >
            <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-full bg-emerald-500/20 border border-emerald-500/40 text-emerald-400 flex items-center justify-center text-base sm:text-lg pointer-events-none">
              <i className="fas fa-plus"></i>
            </div>
          </div>

          {/* Floating Recent Delta Badge */}
          <AnimatePresence>
            {delta && (
              <motion.div
                initial={{ opacity: 0, y: 15, scale: 0.8 }}
                animate={{ opacity: 1, y: -25, scale: 1.1 }}
                exit={{ opacity: 0, y: -40, scale: 0.8 }}
                className={`absolute z-20 pointer-events-none px-2.5 py-0.5 rounded-full text-xs sm:text-sm font-black tracking-wider shadow-lg ${
                  delta.diff > 0
                    ? 'bg-emerald-500 text-black shadow-emerald-500/40'
                    : 'bg-red-500 text-white shadow-red-500/40'
                }`}
              >
                {delta.diff > 0 ? `+${delta.diff}` : delta.diff}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Main Giant Number */}
          <div className="text-center select-none pointer-events-none">
            <div
              className={`font-black tracking-tighter text-white tabular-nums drop-shadow-md leading-none ${
                selectedGame === 'yugioh'
                  ? 'text-5xl sm:text-7xl md:text-8xl'
                  : 'text-7xl sm:text-8xl md:text-9xl'
              }`}
            >
              {val}
            </div>
            <div className="text-[9px] sm:text-[10px] uppercase font-bold tracking-widest text-slate-400/80 mt-1">
              {config.primaryLabel}
            </div>
          </div>
        </div>

        {/* Bottom bar: Quick Controls */}
        <div className="z-10 pt-0.5 shrink-0">{renderQuickButtons(idx)}</div>
      </div>
    );
  };

  // Central Slim Control Bar
  const renderControlBar = () => {
    return (
      <div className="h-9 sm:h-10 bg-slate-900/95 border border-slate-800 rounded-xl px-2 flex items-center justify-between shrink-0 shadow-lg z-20 gap-1">
        {/* Left side: Back to tools + Game Switcher */}
        <div className="flex items-center gap-1 shrink-0">
          {onBack && (
            <button
              onClick={onBack}
              className="h-7 px-2 rounded-lg bg-slate-800/80 hover:bg-slate-750 text-slate-300 hover:text-white flex items-center gap-1 text-[11px] font-bold active:scale-95 transition-all"
              title="Voltar para Ferramentas"
            >
              <i className="fas fa-arrow-left text-[9px]"></i>
              <span className="hidden xs:inline">Voltar</span>
            </button>
          )}

          <button
            onClick={() => setShowGameSelector(true)}
            className={`flex items-center gap-1 h-7 px-2 rounded-lg text-[11px] font-black uppercase tracking-wider border transition-all active:scale-95 ${config.color.badge}`}
          >
            <i className={`fas ${config.icon} text-[10px]`}></i>
            <span className="truncate max-w-[80px] sm:max-w-none">{config.shortName}</span>
            <i className="fas fa-chevron-down text-[7px] opacity-60 ml-0.5"></i>
          </button>
        </div>

        {/* Center: Tools (Reset, Invert, Dice, Sound, History) */}
        <div className="flex items-center gap-1">
          {/* Reset Match */}
          <button
            onClick={resetAll}
            className="w-7 h-7 rounded-lg bg-slate-800/80 hover:bg-slate-750 text-slate-300 hover:text-white flex items-center justify-center text-[11px] active:scale-90 transition-all"
            title="Reiniciar Partida"
          >
            <i className="fas fa-undo-alt"></i>
          </button>

          {/* Flip Top Player (180deg toggle - only in 2P mode) */}
          {playerCount === 2 && (
            <button
              onClick={() => setIsRotatedP2(prev => !prev)}
              className={`w-7 h-7 rounded-lg flex items-center justify-center text-[11px] active:scale-90 transition-all ${
                isRotatedP2
                  ? 'bg-purple-600 text-white shadow-md shadow-purple-600/30'
                  : 'bg-slate-800/80 text-slate-400'
              }`}
              title={isRotatedP2 ? 'Mesa: Oponente Invertido (Frente a Frente)' : 'Modo Normal'}
            >
              <i className="fas fa-rotate"></i>
            </button>
          )}

          {/* Dice / Coin Quick Roller */}
          <button
            onClick={() => setShowQuickDice(true)}
            className="w-7 h-7 rounded-lg bg-slate-800/80 hover:bg-slate-750 text-slate-300 hover:text-white flex items-center justify-center text-[11px] active:scale-90 transition-all"
            title="Rolar Dados / Moeda"
          >
            <i className="fas fa-dice"></i>
          </button>

          {/* Match History */}
          <button
            onClick={() => setShowHistoryModal(true)}
            className="w-7 h-7 rounded-lg bg-slate-800/80 hover:bg-slate-750 text-slate-300 hover:text-white flex items-center justify-center text-[11px] active:scale-90 transition-all"
            title="Histórico de Vidas"
          >
            <i className="fas fa-clock-rotate-left"></i>
          </button>

          {/* Sound Toggle */}
          <button
            onClick={() => setSoundEnabled(prev => !prev)}
            className={`w-7 h-7 rounded-lg flex items-center justify-center text-[11px] active:scale-90 transition-all ${
              soundEnabled ? 'text-slate-300 hover:text-white bg-slate-800/80' : 'text-slate-500 bg-slate-900'
            }`}
            title={soundEnabled ? 'Efeitos Sonoros Ativos' : 'Silencioso'}
          >
            <i className={`fas ${soundEnabled ? 'fa-volume-high' : 'fa-volume-xmark'}`}></i>
          </button>
        </div>

        {/* Right side: Player count & Fullscreen */}
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={() => setPlayerCount(prev => (prev === 2 ? 1 : prev === 1 ? 4 : 2))}
            className="h-7 px-2 bg-slate-800 text-slate-300 hover:text-white rounded-lg text-[10px] font-black uppercase active:scale-95 transition-all"
            title="Alterar número de jogadores"
          >
            {playerCount}P
          </button>

          <button
            onClick={() => setIsFullscreen(prev => !prev)}
            className={`w-7 h-7 rounded-lg flex items-center justify-center text-[11px] active:scale-90 transition-all ${
              isFullscreen
                ? 'bg-emerald-600 text-white'
                : 'bg-slate-800 text-slate-300 hover:text-white'
            }`}
            title={isFullscreen ? 'Sair do Modo Tela Cheia' : 'Modo Tela Cheia (Sem Scroll)'}
          >
            <i className={`fas ${isFullscreen ? 'fa-compress' : 'fa-expand'}`}></i>
          </button>
        </div>
      </div>
    );
  };

  return (
    <div
      className={`flex flex-col select-none overscroll-none touch-manipulation w-full ${
        isFullscreen
          ? 'fixed inset-0 z-[100] bg-slate-950 p-1.5 sm:p-2.5 h-[100dvh] w-screen overflow-hidden'
          : 'fixed inset-0 z-[65] md:relative md:inset-auto md:z-auto h-[100dvh] md:h-[calc(100dvh-130px)] md:max-h-[860px] md:min-h-[560px] bg-slate-950 p-1.5 sm:p-2.5 overflow-hidden shadow-2xl md:rounded-[32px] md:border md:border-slate-800'
      }`}
      style={{ touchAction: 'manipulation' }}
    >
      {/* Digimon Memory Gauge if active */}
      {selectedGame === 'digimon' && (
        <div className="mb-1 p-1 bg-slate-900/90 rounded-xl border border-blue-500/30 flex flex-col items-center shrink-0">
          <div className="flex items-center justify-between w-full text-[9px] font-bold text-slate-400 mb-0.5 px-1">
            <span className="text-blue-400">P1 (Azul)</span>
            <span className="text-white font-black uppercase tracking-wider text-[8px]">Memory Gauge</span>
            <span className="text-amber-400">P2 (Oponente)</span>
          </div>
          <div className="flex items-center justify-between w-full gap-0.5 overflow-x-auto pb-0.5">
            {[-10, -9, -8, -7, -6, -5, -4, -3, -2, -1, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(m => (
              <button
                key={m}
                onClick={() => {
                  triggerHaptic();
                  playSound('calc');
                  setSharedMemory(m);
                }}
                className={`flex-1 min-w-[15px] h-6 rounded text-[9px] font-bold flex items-center justify-center transition-all ${
                  sharedMemory === m
                    ? m === 0
                      ? 'bg-white text-black font-black scale-105 shadow-md'
                      : m < 0
                        ? 'bg-blue-500 text-white font-black scale-105 shadow-md shadow-blue-500/40'
                        : 'bg-amber-500 text-black font-black scale-105 shadow-md shadow-amber-500/40'
                    : m === 0
                      ? 'bg-slate-700 text-slate-200'
                      : m < 0
                        ? 'bg-blue-950/60 text-blue-400 hover:bg-blue-900/60'
                        : 'bg-amber-950/60 text-amber-400 hover:bg-amber-900/60'
                }`}
              >
                {m}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Main Boards */}
      <div className="flex-1 flex flex-col gap-1.5 min-h-0">
        {playerCount === 1 && (
          <>
            {renderControlBar()}
            {renderPlayerZone(0, false)}
          </>
        )}

        {playerCount === 2 && (
          <>
            {/* Player 2 (Top - optionally inverted 180deg) */}
            {renderPlayerZone(1, true)}

            {/* Central Slim Control Hub (Divider) */}
            {renderControlBar()}

            {/* Player 1 (Bottom - normal orientation) */}
            {renderPlayerZone(0, false)}
          </>
        )}

        {playerCount === 4 && (
          <>
            {renderControlBar()}
            <div className="flex-1 grid grid-cols-2 gap-1.5 min-h-0">
              {renderPlayerZone(1, true)}
              {renderPlayerZone(2, true)}
              {renderPlayerZone(0, false)}
              {renderPlayerZone(3, false)}
            </div>
          </>
        )}
      </div>

      {/* Game Selector Modal */}
      <AnimatePresence>
        {showGameSelector && (
          <div className="fixed inset-0 z-[110] bg-black/85 backdrop-blur-md flex items-center justify-center p-3 sm:p-4 overflow-y-auto">
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-slate-900 border border-slate-800 rounded-3xl p-4 sm:p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto space-y-4 shadow-2xl relative my-auto"
            >
              <button
                onClick={() => setShowGameSelector(false)}
                className="absolute top-4 right-4 z-20 w-8 h-8 rounded-full bg-slate-800 text-slate-400 hover:text-white flex items-center justify-center text-xs transition-colors border border-white/10"
                title="Fechar seletor"
              >
                <i className="fas fa-times"></i>
              </button>

              <GameSelector
                onSelectGame={(gameId, game) => {
                  const targetKey = mapActiveGameToKey(game?.slug || gameId);
                  setSelectedGame(targetKey);
                  setShowGameSelector(false);
                }}
                title="Escolha o Card Game"
                subtitle="Regras e contadores específicos por franquia de TCG:"
                className="py-2"
              />

              {/* Presets for current game */}
              {config.presets && config.presets.length > 1 && (
                <div className="pt-4 border-t border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 px-2">
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                    Formato / Vida Inicial de {config.name}:
                  </span>
                  <div className="flex gap-2 flex-wrap">
                    {config.presets.map(val => (
                      <button
                        key={val}
                        onClick={() => {
                          setLife(Array(4).fill(val));
                          setShowGameSelector(false);
                        }}
                        className="px-3 py-1.5 rounded-xl bg-slate-950 border border-slate-800 hover:border-purple-500 text-xs font-bold text-white transition-all shadow"
                      >
                        {val} {config.primaryLabel}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Calculator Keypad Modal */}
      <AnimatePresence>
        {calculatorPlayerIndex !== null && (
          <div className="fixed inset-0 z-[110] bg-black/85 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-slate-900 border border-slate-800 rounded-3xl p-4 w-full max-w-xs space-y-3 shadow-2xl"
            >
              <div className="flex items-center justify-between pb-1.5 border-b border-white/5">
                <div>
                  <h3 className="text-xs font-black text-white uppercase tracking-tight">
                    Calculadora • {names[calculatorPlayerIndex]}
                  </h3>
                  <span className="text-[10px] text-slate-400">
                    Atual:{' '}
                    <strong className="text-white font-black">
                      {life[calculatorPlayerIndex]}
                    </strong>
                  </span>
                </div>
                <button
                  onClick={() => setCalculatorPlayerIndex(null)}
                  className="w-6 h-6 rounded-full bg-slate-800 text-slate-400 hover:text-white flex items-center justify-center text-xs"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>

              {/* Input Display */}
              <div className="bg-slate-950 border border-slate-800 rounded-2xl p-2.5 text-right min-h-[46px] flex items-center justify-end">
                <span className="text-2xl font-black text-white font-mono tracking-wider tabular-nums">
                  {calcInput || '0'}
                </span>
              </div>

              {/* Keypad */}
              <div className="grid grid-cols-3 gap-1.5">
                {['7', '8', '9', '4', '5', '6', '1', '2', '3', 'C', '0', '00'].map(k => (
                  <button
                    key={k}
                    onClick={() => {
                      triggerHaptic();
                      playSound('calc');
                      if (k === 'C') {
                        setCalcInput('');
                      } else {
                        setCalcInput(prev => (prev.length < 6 ? prev + k : prev));
                      }
                    }}
                    className={`h-11 rounded-xl text-base font-black transition-all active:scale-95 ${
                      k === 'C'
                        ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                        : 'bg-slate-800/80 hover:bg-slate-700 text-white'
                    }`}
                  >
                    {k}
                  </button>
                ))}
              </div>

              {/* Quick actions for damage or heal */}
              <div className="grid grid-cols-2 gap-2 pt-1">
                <button
                  onClick={() => handleCalcApply(true)}
                  disabled={!calcInput || parseInt(calcInput, 10) <= 0}
                  className="py-2.5 rounded-xl bg-red-600 hover:bg-red-500 disabled:opacity-40 text-white text-xs font-black uppercase tracking-wider flex items-center justify-center gap-1.5 active:scale-95 transition-all"
                >
                  <i className="fas fa-minus"></i> Dano
                </button>
                <button
                  onClick={() => handleCalcApply(false)}
                  disabled={!calcInput || parseInt(calcInput, 10) <= 0}
                  className="py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white text-xs font-black uppercase tracking-wider flex items-center justify-center gap-1.5 active:scale-95 transition-all"
                >
                  <i className="fas fa-plus"></i> Curar
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Match History Modal */}
      <AnimatePresence>
        {showHistoryModal && (
          <div className="fixed inset-0 z-[110] bg-black/80 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-slate-900 border border-slate-800 rounded-3xl p-4 sm:p-5 w-full max-w-sm max-h-[80vh] flex flex-col space-y-3 shadow-2xl"
            >
              <div className="flex items-center justify-between pb-2 border-b border-white/5">
                <div>
                  <h3 className="text-sm font-black text-white uppercase tracking-tight">
                    Histórico da Partida
                  </h3>
                  <p className="text-[10px] text-slate-400">Registro de todas as alterações de vida</p>
                </div>
                <button
                  onClick={() => setShowHistoryModal(false)}
                  className="w-7 h-7 rounded-full bg-slate-800 text-slate-400 hover:text-white flex items-center justify-center text-xs"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>

              <div className="flex-1 overflow-y-auto space-y-1.5 pr-1 min-h-[160px]">
                {historyLogs.length === 0 ? (
                  <div className="text-center py-8 text-xs text-slate-500">
                    Nenhuma alteração registrada ainda.
                  </div>
                ) : (
                  historyLogs.map(log => (
                    <div
                      key={log.id}
                      className="p-2 bg-slate-950 rounded-xl border border-slate-800/80 flex items-center justify-between text-xs"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-slate-500 font-mono">{log.time}</span>
                        <span className="font-bold text-white truncate max-w-[100px]">
                          {log.playerName}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span
                          className={`font-black text-xs ${
                            log.diff > 0 ? 'text-emerald-400' : 'text-red-400'
                          }`}
                        >
                          {log.diff > 0 ? `+${log.diff}` : log.diff}
                        </span>
                        <span className="text-[10px] text-slate-400">
                          ({log.newLife})
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {historyLogs.length > 0 && (
                <button
                  onClick={() => setHistoryLogs([])}
                  className="w-full py-2 bg-slate-800/80 hover:bg-slate-750 text-slate-300 text-xs font-bold rounded-xl transition-all"
                >
                  Limpar Histórico
                </button>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Dice & Coin Quick Roller Modal */}
      <AnimatePresence>
        {showQuickDice && (
          <div className="fixed inset-0 z-[110] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-slate-900 border border-slate-800 rounded-3xl p-5 w-full max-w-sm text-center space-y-4 shadow-2xl"
            >
              <div className="flex items-center justify-between pb-2 border-b border-white/5">
                <h3 className="text-sm font-black text-white uppercase tracking-tight">Sorteador Rápido</h3>
                <button
                  onClick={() => setShowQuickDice(false)}
                  className="w-7 h-7 rounded-full bg-slate-800 text-slate-400 hover:text-white flex items-center justify-center text-xs"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>

              <div className="flex items-center justify-center gap-3 py-1">
                <div className="flex-1 bg-slate-950 border border-slate-800 rounded-2xl p-3 flex flex-col items-center justify-center min-h-[85px]">
                  <span className="text-[10px] font-black uppercase text-slate-500 mb-1">Dado</span>
                  <span className="text-3xl font-black text-purple-400">{diceResult ?? '-'}</span>
                </div>
                <div className="flex-1 bg-slate-950 border border-slate-800 rounded-2xl p-3 flex flex-col items-center justify-center min-h-[85px]">
                  <span className="text-[10px] font-black uppercase text-slate-500 mb-1">Moeda</span>
                  <span
                    className={`text-lg font-black ${
                      coinResult === 'CARA' ? 'text-yellow-400' : 'text-slate-300'
                    }`}
                  >
                    {coinResult ?? '-'}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2">
                <button
                  onClick={() => rollDie(20)}
                  className="py-2.5 bg-purple-600 hover:bg-purple-500 text-white rounded-xl text-xs font-black uppercase active:scale-95 transition-all"
                >
                  D20
                </button>
                <button
                  onClick={() => rollDie(6)}
                  className="py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-xs font-black uppercase active:scale-95 transition-all"
                >
                  D6
                </button>
                <button
                  onClick={flipCoin}
                  className="py-2.5 bg-yellow-600 hover:bg-yellow-500 text-white rounded-xl text-xs font-black uppercase active:scale-95 transition-all"
                >
                  Moeda
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Extra Counters Modal for a specific player */}
      <AnimatePresence>
        {activeExtraModal !== null && (
          <div className="fixed inset-0 z-[110] bg-black/80 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-slate-900 border border-slate-800 rounded-3xl p-5 w-full max-w-sm space-y-4 max-h-[85vh] overflow-y-auto shadow-2xl"
            >
              <div className="flex items-center justify-between pb-2 border-b border-white/5">
                <div>
                  <h3 className="text-sm font-black text-white uppercase tracking-tight">
                    Marcadores • {names[activeExtraModal]}
                  </h3>
                  <p className="text-[10px] text-slate-400">Contadores específicos e regras</p>
                </div>
                <button
                  onClick={() => setActiveExtraModal(null)}
                  className="w-7 h-7 rounded-full bg-slate-800 text-slate-400 hover:text-white flex items-center justify-center text-xs"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>

              {/* Godzilla Specific */}
              {selectedGame === 'godzilla' && (
                <div className="space-y-3">
                  <div className="p-3 bg-slate-950 rounded-2xl border border-slate-800 space-y-2">
                    <span className="text-xs font-bold text-white block">Rank do Monstro Ativo</span>
                    <div className="grid grid-cols-4 gap-1.5">
                      {[1, 2, 3, 4].map(rank => (
                        <button
                          key={rank}
                          onClick={() =>
                            updateExtra(activeExtraModal, p => ({ ...p, monsterRank: rank }))
                          }
                          className={`py-2 rounded-xl text-xs font-black transition-all ${
                            extra[activeExtraModal].monsterRank === rank
                              ? 'bg-emerald-500 text-black shadow-lg shadow-emerald-500/30'
                              : 'bg-slate-900 text-slate-400 hover:bg-slate-800'
                          }`}
                        >
                          Rank {rank === 1 ? 'I' : rank === 2 ? 'II' : rank === 3 ? 'III' : 'IV'}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-slate-950 rounded-2xl border border-slate-800">
                    <div className="flex items-center gap-2">
                      <i className="fas fa-bolt text-teal-400"></i>
                      <span className="text-xs font-bold text-white">Energia / Fúria</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() =>
                          updateExtra(activeExtraModal, p => ({
                            ...p,
                            godzillaEnergy: Math.max(0, p.godzillaEnergy - 1)
                          }))
                        }
                        className="w-7 h-7 rounded bg-slate-800 text-white font-bold"
                      >
                        -
                      </button>
                      <span className="text-sm font-black text-teal-400 w-6 text-center">
                        {extra[activeExtraModal].godzillaEnergy}
                      </span>
                      <button
                        onClick={() =>
                          updateExtra(activeExtraModal, p => ({
                            ...p,
                            godzillaEnergy: p.godzillaEnergy + 1
                          }))
                        }
                        className="w-7 h-7 rounded bg-slate-800 text-white font-bold"
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Magic: Poison, Commander, Energy */}
              {selectedGame === 'magic' && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-slate-950 rounded-2xl border border-slate-800">
                    <div className="flex items-center gap-2">
                      <i className="fas fa-skull-crossbones text-emerald-400"></i>
                      <span className="text-xs font-bold text-white">Marcadores de Veneno</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() =>
                          updateExtra(activeExtraModal, p => ({
                            ...p,
                            poison: Math.max(0, p.poison - 1)
                          }))
                        }
                        className="w-7 h-7 rounded bg-slate-800 text-white font-bold"
                      >
                        -
                      </button>
                      <span className="text-sm font-black text-emerald-400 w-6 text-center">
                        {extra[activeExtraModal].poison}
                      </span>
                      <button
                        onClick={() =>
                          updateExtra(activeExtraModal, p => ({ ...p, poison: p.poison + 1 }))
                        }
                        className="w-7 h-7 rounded bg-slate-800 text-white font-bold"
                      >
                        +
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-slate-950 rounded-2xl border border-slate-800">
                    <div className="flex items-center gap-2">
                      <i className="fas fa-crown text-red-400"></i>
                      <span className="text-xs font-bold text-white">Dano de Comandante</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() =>
                          updateExtra(activeExtraModal, p => ({
                            ...p,
                            commanderDamage: Math.max(0, p.commanderDamage - 1)
                          }))
                        }
                        className="w-7 h-7 rounded bg-slate-800 text-white font-bold"
                      >
                        -
                      </button>
                      <span className="text-sm font-black text-red-400 w-6 text-center">
                        {extra[activeExtraModal].commanderDamage}
                      </span>
                      <button
                        onClick={() =>
                          updateExtra(activeExtraModal, p => ({
                            ...p,
                            commanderDamage: p.commanderDamage + 1
                          }))
                        }
                        className="w-7 h-7 rounded bg-slate-800 text-white font-bold"
                      >
                        +
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-slate-950 rounded-2xl border border-slate-800">
                    <div className="flex items-center gap-2">
                      <i className="fas fa-bolt text-cyan-400"></i>
                      <span className="text-xs font-bold text-white">Marcadores de Energia</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() =>
                          updateExtra(activeExtraModal, p => ({
                            ...p,
                            energy: Math.max(0, p.energy - 1)
                          }))
                        }
                        className="w-7 h-7 rounded bg-slate-800 text-white font-bold"
                      >
                        -
                      </button>
                      <span className="text-sm font-black text-cyan-400 w-6 text-center">
                        {extra[activeExtraModal].energy}
                      </span>
                      <button
                        onClick={() =>
                          updateExtra(activeExtraModal, p => ({ ...p, energy: p.energy + 1 }))
                        }
                        className="w-7 h-7 rounded bg-slate-800 text-white font-bold"
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Pokemon: Conditions & VSTAR */}
              {selectedGame === 'pokemon' && (
                <div className="space-y-3">
                  <div className="p-3 bg-slate-950 rounded-2xl border border-slate-800 space-y-2">
                    <span className="text-xs font-bold text-white block">Condições Especiais</span>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { key: 'poison', label: 'Envenenado', icon: 'fa-skull' },
                        { key: 'burn', label: 'Queimado', icon: 'fa-fire' },
                        { key: 'asleep', label: 'Adormecido', icon: 'fa-moon' },
                        { key: 'confused', label: 'Confuso', icon: 'fa-question' },
                        { key: 'paralyzed', label: 'Paralisado', icon: 'fa-bolt' }
                      ].map(c => {
                        const active = (extra[activeExtraModal].specialConditions as any)[c.key];
                        return (
                          <button
                            key={c.key}
                            onClick={() =>
                              updateExtra(activeExtraModal, p => ({
                                ...p,
                                specialConditions: {
                                  ...p.specialConditions,
                                  [c.key]: !active
                                }
                              }))
                            }
                            className={`p-2 rounded-xl border text-xs font-bold flex items-center gap-2 transition-all ${
                              active
                                ? 'bg-purple-600/30 border-purple-500 text-white'
                                : 'bg-slate-900 border-slate-800 text-slate-400'
                            }`}
                          >
                            <i className={`fas ${c.icon}`}></i> {c.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-slate-950 rounded-2xl border border-slate-800">
                    <span className="text-xs font-bold text-white">Marcador VSTAR / GX</span>
                    <button
                      onClick={() =>
                        updateExtra(activeExtraModal, p => ({ ...p, vstarUsed: !p.vstarUsed }))
                      }
                      className={`px-3 py-1.5 rounded-xl text-xs font-black uppercase transition-all ${
                        extra[activeExtraModal].vstarUsed
                          ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                          : 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                      }`}
                    >
                      {extra[activeExtraModal].vstarUsed ? 'Já Usado' : 'Disponível'}
                    </button>
                  </div>
                </div>
              )}

              {/* One Piece: DON!! and Power */}
              {selectedGame === 'one-piece' && (
                <div className="space-y-3">
                  <div className="p-3 bg-slate-950 rounded-2xl border border-slate-800 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-white">DON!! Ativo</span>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() =>
                            updateExtra(activeExtraModal, p => ({
                              ...p,
                              donActive: Math.max(0, p.donActive - 1)
                            }))
                          }
                          className="w-7 h-7 rounded bg-slate-800 text-white font-bold"
                        >
                          -
                        </button>
                        <span className="text-sm font-black text-red-400 w-6 text-center">
                          {extra[activeExtraModal].donActive}
                        </span>
                        <button
                          onClick={() =>
                            updateExtra(activeExtraModal, p => ({
                              ...p,
                              donActive: Math.min(10, p.donActive + 1)
                            }))
                          }
                          className="w-7 h-7 rounded bg-slate-800 text-white font-bold"
                        >
                          +
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Star Wars */}
              {selectedGame === 'star-wars' && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-slate-950 rounded-2xl border border-slate-800">
                    <span className="text-xs font-bold text-white">Iniciativa</span>
                    <button
                      onClick={() =>
                        updateExtra(activeExtraModal, p => ({ ...p, initiative: !p.initiative }))
                      }
                      className={`px-3 py-1.5 rounded-xl text-xs font-black uppercase transition-all ${
                        extra[activeExtraModal].initiative
                          ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
                          : 'bg-slate-800 text-slate-400'
                      }`}
                    >
                      {extra[activeExtraModal].initiative ? '✦ Comigo' : 'Não tem'}
                    </button>
                  </div>
                </div>
              )}

              {/* Grand Archive */}
              {selectedGame === 'grand-archive' && (
                <div className="space-y-3">
                  <div className="p-3 bg-slate-950 rounded-2xl border border-slate-800 space-y-2">
                    <span className="text-xs font-bold text-white block">Nível do Campeão</span>
                    <div className="grid grid-cols-4 gap-1.5">
                      {[0, 1, 2, 3].map(lvl => (
                        <button
                          key={lvl}
                          onClick={() =>
                            updateExtra(activeExtraModal, p => ({ ...p, championLevel: lvl }))
                          }
                          className={`py-2 rounded-xl text-xs font-black transition-all ${
                            extra[activeExtraModal].championLevel === lvl
                              ? 'bg-indigo-500 text-white shadow-lg shadow-indigo-500/30'
                              : 'bg-slate-900 text-slate-400 hover:bg-slate-800'
                          }`}
                        >
                          Nível {lvl}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-slate-950 rounded-2xl border border-slate-800">
                    <span className="text-xs font-bold text-white">Memória</span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() =>
                          updateExtra(activeExtraModal, p => ({
                            ...p,
                            memory: Math.max(0, p.memory - 1)
                          }))
                        }
                        className="w-7 h-7 rounded bg-slate-800 text-white font-bold"
                      >
                        -
                      </button>
                      <span className="text-sm font-black text-indigo-400 w-6 text-center">
                        {extra[activeExtraModal].memory}
                      </span>
                      <button
                        onClick={() =>
                          updateExtra(activeExtraModal, p => ({
                            ...p,
                            memory: p.memory + 1
                          }))
                        }
                        className="w-7 h-7 rounded bg-slate-800 text-white font-bold"
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <button
                onClick={() => setActiveExtraModal(null)}
                className="w-full py-2.5 bg-purple-600 hover:bg-purple-500 text-white font-black text-xs uppercase tracking-wider rounded-xl transition-all"
              >
                Concluído
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};
